import { Controller } from '@hotwired/stimulus'
import { addClass } from 'utils/toggle_class'
import { hideElement, showElement } from 'utils/toggle_element_visibility'
import Quimbee from 'quimbee'

export default class extends Controller {
  static targets = ["searchInput", "item", "itemTitle", "content"]
  static values = {
    currentItemId: String,
    rendererType: String
  }

  initialize() {
    const data = Quimbee.data.courseSidebar || {}

    this.userData = data.jsonCache
    this.chapterIds = data.courseData.chapterIds || {}

    this.chapterDomMap = data.domMaps.chapters || {}
    this.lessonDomMap = data.domMaps.lessons || {}

    this.chapterAccessMap = data.accessData.chapters || {}
    this.lessonAccessMap = data.accessData.lessons || {}
    this.canBookmark = data.accessData.canBookmark
    this.hasFlashcardsAccess = data.accessData.hasFlashcardsAccess

    this._addCurrentItemClass()
    this._renderFromCache()
  }


  filterItems() {
    const itemTitles = this.itemTitleTargets
    const searchString = this.searchInputTarget.value.toUpperCase()

    this.itemTargets.forEach((item) => {
      const titleElement = itemTitles.find((itemTitle) => item.contains(itemTitle))
      const title = titleElement.innerText.toUpperCase()

      if(title.indexOf(searchString) === -1) {
        hideElement(item)
      } else {
        showElement(item)
      }
    })
  }

  // This is so we can use Russian Doll caching ignoring which video is
  // currently selected
  _addCurrentItemClass() {
    const currentItem = this.itemTargets.find((item) => item.id === this.currentItemIdValue)
    addClass(currentItem, "lesson-selected")

    let topOffset = this._getTopOffset(currentItem)
    topOffset -= this._getTopOffset(this.contentTarget)

    this.contentTarget.scrollTop = topOffset
  }

  // https://stackoverflow.com/a/60083373/1202488
  _getTopOffset(element) {
    const rect = element.getBoundingClientRect()
    const win = element.ownerDocument.defaultView
    return rect.top + win.pageYOffset
  }

  _renderFromCache() {
    Object.keys(this.chapterIds).forEach((chapterId) => {
      const lessonIds = this.chapterIds[chapterId]
      const chapter = this.userData[chapterId]
      this._updateChapter(chapterId, chapter, lessonIds)
    })
  }

  _updateChapter(chapterId, chapter, lessonIds) {
    const chapterEl = this.element.querySelector(`#${this.chapterDomMap[chapterId]}`)
    const canTakeChapter = this.chapterAccessMap[chapterId]
    this._hideNoAccessItems(chapterEl, canTakeChapter)

    const chapterQuizEl = chapterEl.querySelector('[data-js-chapter-quiz-link]')
    this._updateQuizzableQuiz(chapterQuizEl, chapter && chapter.quiz, canTakeChapter)
    for (let i = 0; i < lessonIds.length; i++) {
      const lessonId = lessonIds[i]
      const lesson = chapter && chapter.lessons[lessonId]
      this._updateLesson(lessonId, lesson, canTakeChapter)
    }
    this._updateFlashcardsAccess(chapterEl)
  }

  _hideNoAccessItems(chapterEl, canTakeChapter){
    const withAccessItems = this._findCsrElements(chapterEl, 'with-access-to-chapter')
    const withoutAccessItems = this._findCsrElements(chapterEl, 'without-access-to-chapter')
    if (canTakeChapter) {
      showElement(withAccessItems)
      hideElement(withoutAccessItems)
    } else {
      hideElement(withAccessItems)
      showElement(withoutAccessItems)
    }
  }

  _updateLesson(lessonId, lesson, hasChapterAccess) {
    const hasLessonAccess = this.lessonAccessMap[lessonId]
    const lessonEl = this.element.querySelector(`#${this.lessonDomMap[lessonId]}`)
    if (lesson) {
      this._updateQuizzableQuiz(lessonEl, lesson.quiz, hasChapterAccess)
      this._updateBookmark(lessonEl, lesson.bookmarked, hasLessonAccess, this.canBookmark)
      this._updateViewed(lessonEl, lesson.viewed)
    } else {
      this._updateBookmark(lessonEl, false, hasLessonAccess, this.canBookmark)
      this._updateViewed(lessonEl, false)
      this._updateQuizzableQuiz(lessonEl, null, hasChapterAccess)
    }
  }

  _updateBookmark(container, bookmarkData, canAccessBookmarkable, canBookmark) {
    const hasAccessToBookmarkable = this._findCsrElements(container, 'with-access-to-bookmarkable')
    const noAccessToBookmarkable = this._findCsrElements(container, 'without-access-to-bookmarkable')
    const forExistingBookmarkItems = this._findCsrElements(container, 'with-existing-bookmark')
    const forNewBookmarkItems = this._findCsrElements(container, 'without-existing-bookmark')

    // bookmarkData can either be true/false (pre-folders) or an array (post-folders), or null if not signed in
    let isBookmarked = null
    if(bookmarkData === null || bookmarkData === undefined || typeof(bookmarkData) === "boolean")
      isBookmarked = bookmarkData
    else
      isBookmarked = bookmarkData.length > 0

    hideElement(hasAccessToBookmarkable)
    hideElement(noAccessToBookmarkable)
    hideElement(forExistingBookmarkItems)
    hideElement(forNewBookmarkItems)

    if (canAccessBookmarkable) {
      showElement(hasAccessToBookmarkable)
      if (canBookmark) {
        if (isBookmarked) {
          showElement(forExistingBookmarkItems)
        } else {
          showElement(forNewBookmarkItems)
        }
      }
    } else {
      showElement(noAccessToBookmarkable)
    }
  }

  _updateViewed(container, viewed) {
    if (viewed) {
      showElement(this._findCsrElements(container, 'content-viewed'))
    } else {
      hideElement(this._findCsrElements(container, 'content-viewed'))
    }
  }

  _updateQuizzableQuiz(quizEl, quizData, hasAccess) {
    const withFinishedQuiz = this._findCsrElements(quizEl, 'with-finished-quiz-attempt')
    const withoutFinishedQuiz = this._findCsrElements(quizEl, 'without-finished-quiz-attempt')
    const withUnfinishedQuiz = this._findCsrElements(quizEl, 'with-unfinished-quiz-attempt')
    const withoutUnfinishedQuiz = this._findCsrElements(quizEl, 'without-unfinished-quiz-attempt')

    if (hasAccess && (quizData && quizData.attempted)) {
      if (quizData.finished) {
        showElement(withFinishedQuiz)
        hideElement(withoutFinishedQuiz)

        this._updateAndShowCsrElements(quizEl, 'attempt-questions-correct', quizData.correct_count)
        this._updateAndShowCsrElements(quizEl, 'attempt-questions-total', quizData.total_count)
      } else {
        hideElement(withFinishedQuiz)
        showElement(withoutFinishedQuiz)
        showElement(withUnfinishedQuiz)
        hideElement(withoutUnfinishedQuiz)
      }
    } else {
      hideElement(withFinishedQuiz)
      showElement(withoutFinishedQuiz)
      hideElement(withUnfinishedQuiz)
      showElement(withoutUnfinishedQuiz)
    }
  }

  _updateAndShowCsrElements(quizEl, csrName, newText) {
    const elements = this._findCsrElements(quizEl, csrName)
    elements.forEach((el) => { el.innerText = newText })
    showElement(elements)
  }

  _updateFlashcardsAccess(chapterEl){
    if (this.hasFlashcardsAccess) {
      showElement(this._findCsrElements(chapterEl, 'with-access-to-flashcards'))
      hideElement(this._findCsrElements(chapterEl, 'without-access-to-flashcards'))
    } else {
      hideElement(this._findCsrElements(chapterEl, 'with-access-to-flashcards'))
      showElement(this._findCsrElements(chapterEl, 'without-access-to-flashcards'))
    }
  }

  _findCsrElements(container, csrName) {
    if (!container) { return null }
    return container.querySelectorAll('[data-csr~=\'' + csrName + '\']')
  }
}
